import * as React from "react";
import { useEffect } from "react";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  side?: "left" | "right";
};

const overlayStyle = (isOpen: boolean) => ({
  position: "fixed" as const,
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0, 0, 0, 0.5)",
  opacity: isOpen ? 1 : 0,
  visibility: (isOpen ? "visible" : "hidden") as "visible" | "hidden",
  transition: "opacity 0.3s ease, visibility 0.3s ease",
  zIndex: 999
});

const drawerStyle = (isOpen: boolean, side: "left" | "right" = "left") => {
  const sidePosition = side === "left" ? { left: 0 } : { right: 0 };
  const transform = isOpen ? "translateX(0)" : side === "left" ? "translateX(-100%)" : "translateX(100%)";

  return {
    position: "fixed" as const,
    top: 0,
    bottom: 0,
    width: "280px",
    maxWidth: "85vw",
    backgroundColor: "white",
    boxShadow: "2px 0 8px rgba(0, 0, 0, 0.15)",
    transform,
    transition: "transform 0.3s ease",
    zIndex: 1000,
    overflowY: "auto" as const,
    ...sidePosition
  };
};

const Drawer = ({ isOpen, onClose, children, side = "left" }: Props): React.ReactNode => {
  // Close drawer on Escape key
  useEffect(() => {
    const handleEscape = (e: Event) => {
      if (e instanceof KeyboardEvent && e.key === "Escape" && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      // Prevent body scroll when drawer is open
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose]);

  return (
    <>
      <div css={overlayStyle(isOpen)} onClick={onClose} />
      <div css={drawerStyle(isOpen, side)}>{children}</div>
    </>
  );
};

export default Drawer;
